/**
 * DiagnosticCollector - Tier 2 링 버퍼 관리 싱글톤
 *
 * 앱 부팅 시 init()을 호출하여 console, window.onerror, fetch를
 * 오버라이드하고, 최근 로그/에러/네트워크 에러를 링 버퍼에 수집합니다.
 * 피드백 제출 시 snapshot()으로 현재 버퍼 상태를 반환합니다.
 *
 * 민감 정보(토큰, 패스워드 등)는 자동 마스킹됩니다.
 */

import type {
  DiagnosticLogEntry,
  DiagnosticErrorEntry,
  DiagnosticNetworkEntry,
  FeedbackDiagnostics,
} from '../models/Feedback';

// ── 버퍼 크기 상수 ──
const MAX_CONSOLE_LOGS = 100;
const MAX_JS_ERRORS = 20;
const MAX_NETWORK_ERRORS = 20;

// ── 민감 키워드 (마스킹 대상) ──
const SENSITIVE_PATTERNS = [
  /token[s]?[=:]\s*["']?[A-Za-z0-9._\-]{20,}/gi,
  /bearer\s+[A-Za-z0-9._\-]+/gi,
  /password[=:]\s*["']?[^\s"']+/gi,
  /secret[=:]\s*["']?[^\s"']+/gi,
  /apikey[=:]\s*["']?[^\s"']+/gi,
  /api[_-]?key[=:]\s*["']?[^\s"']+/gi,
];

class DiagnosticCollector {
  private consoleLogs: DiagnosticLogEntry[] = [];
  private jsErrors: DiagnosticErrorEntry[] = [];
  private networkErrors: DiagnosticNetworkEntry[] = [];

  private originalConsole = {
    log: console.log,
    warn: console.warn,
    error: console.error,
  };
  private originalFetch: typeof window.fetch | null = null;
  private initialized = false;

  /**
   * 앱 부팅 시 가장 먼저 호출.
   * console, window.onerror, fetch를 오버라이드하여 진단 데이터 수집 시작.
   */
  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.overrideConsole();
    this.captureJsErrors();
    this.overrideFetch();
  }

  /**
   * 피드백 제출 시 현재 버퍼의 스냅샷을 반환.
   * 원본 배열을 복사하여 반환하므로 이후 수집에 영향 없음.
   */
  snapshot(): FeedbackDiagnostics {
    return {
      consoleLogs: [...this.consoleLogs],
      jsErrors: [...this.jsErrors],
      networkErrors: [...this.networkErrors],
      collectedAt: Date.now(),
    };
  }

  // ── console.log/warn/error 오버라이드 ──
  private overrideConsole(): void {
    const levels: Array<'log' | 'warn' | 'error'> = ['log', 'warn', 'error'];

    levels.forEach(level => {
      const original = this.originalConsole[level];

      (console as any)[level] = (...args: any[]) => {
        // 무한 재귀 방지: 자체 로그는 수집하지 않음
        const message = this.argsToString(args);
        this.pushToRingBuffer(this.consoleLogs, MAX_CONSOLE_LOGS, {
          level,
          message: this.sanitize(message),
          timestamp: Date.now(),
        });

        // 원래 console 함수 호출
        original.apply(console, args);
      };
    });
  }

  // ── window.onerror + unhandledrejection 캡처 ──
  private captureJsErrors(): void {
    window.addEventListener('error', (e) => {
      this.pushToRingBuffer(this.jsErrors, MAX_JS_ERRORS, {
        message: this.sanitize(e.message || 'Unknown error'),
        source: e.filename,
        lineno: e.lineno,
        colno: e.colno,
        stack: this.sanitize((e.error as Error)?.stack || ''),
        timestamp: Date.now(),
      });
    });

    window.addEventListener('unhandledrejection', (e) => {
      const reason = e.reason;
      this.pushToRingBuffer(this.jsErrors, MAX_JS_ERRORS, {
        message: this.sanitize(String(reason?.message || reason || 'Unhandled rejection')),
        stack: this.sanitize(reason?.stack || ''),
        timestamp: Date.now(),
      });
    });
  }

  // ── fetch 오버라이드 (실패한 요청만 기록) ──
  private overrideFetch(): void {
    this.originalFetch = window.fetch;

    window.fetch = async (...args: Parameters<typeof fetch>) => {
      const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request)?.url || '';
      const method = (args[1]?.method || 'GET').toUpperCase();

      try {
        const response = await this.originalFetch!.apply(window, args);
        if (!response.ok) {
          this.pushToRingBuffer(this.networkErrors, MAX_NETWORK_ERRORS, {
            url: this.truncateUrl(url),
            method,
            status: response.status,
            statusText: response.statusText || '',
            timestamp: Date.now(),
          });
        }
        return response;
      } catch (err) {
        this.pushToRingBuffer(this.networkErrors, MAX_NETWORK_ERRORS, {
          url: this.truncateUrl(url),
          method,
          status: 0, // network failure
          statusText: String(err),
          timestamp: Date.now(),
        });
        throw err;
      }
    };
  }

  // ── 링 버퍼 push (FIFO) ──
  private pushToRingBuffer<T>(buffer: T[], maxSize: number, entry: T): void {
    buffer.push(entry);
    if (buffer.length > maxSize) {
      buffer.shift();
    }
  }

  // ── args를 문자열로 변환 ──
  private argsToString(args: any[]): string {
    return args.map(a => {
      if (a === undefined) return 'undefined';
      if (a === null) return 'null';
      if (typeof a === 'object') {
        try {
          return JSON.stringify(a, null, 0);
        } catch {
          return '[Circular/Unserializable]';
        }
      }
      return String(a);
    }).join(' ');
  }

  // ── 민감 정보 마스킹 ──
  private sanitize(message: string): string {
    let sanitized = message;
    for (const pattern of SENSITIVE_PATTERNS) {
      sanitized = sanitized.replace(pattern, '[REDACTED]');
    }
    // 메시지 길이 제한 (단일 로그가 너무 길면 잘라냄)
    if (sanitized.length > 2000) {
      sanitized = sanitized.substring(0, 2000) + '...[truncated]';
    }
    return sanitized;
  }

  // ── URL 잘라내기 (쿼리 파라미터 제거 + 길이 제한) ──
  private truncateUrl(url: string): string {
    try {
      const parsed = new URL(url);
      const truncated = `${parsed.origin}${parsed.pathname}`;
      return truncated.length > 200 ? truncated.substring(0, 200) + '...' : truncated;
    } catch {
      return url.length > 200 ? url.substring(0, 200) + '...' : url;
    }
  }
}

export const diagnosticCollector = new DiagnosticCollector();
